import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, TrendingUp, Users, Zap, Loader2, ExternalLink, Award,
  CalendarClock, DollarSign, Package, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return iso; }
};

export default function BusinessCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/business/apps-kpis")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur chargement"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const apps = data?.apps || [];
  const global = data?.global || {};

  return (
    <ConsoleShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-8" data-testid="business-center-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-1 inline-flex items-center gap-2">
              <Briefcase className="w-3 h-3" />
              JÉRÔME · Business Center
            </div>
            <h1 className="text-3xl sm:text-4xl font-light text-white tracking-tight">
              Toutes tes apps.<br />
              <span className="text-zinc-500">Une seule vue commerciale.</span>
            </h1>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
              Chaque application reste totalement indépendante côté utilisateur.
              Ici, tu pilotes leur commercial : abonnements, licences, essais, CA, MRR, relances.
            </p>
          </div>
          <Link
            to="/app/settings"
            data-testid="add-app-link"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:bg-white/5 transition"
          >
            <Package className="w-4 h-4" />
            Gérer les apps
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement…
          </div>
        ) : (
          <>
            {/* Global roll-up strip */}
            <div className="border border-white/10 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-6" data-testid="global-rollup">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-3">Consolidé — Toutes apps</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <RollUpKPI label="MRR global" value={fmtEUR(global.mrr)} icon={TrendingUp} accent="#F59E0B" />
                <RollUpKPI label="ARR global" value={fmtEUR(global.arr)} icon={Zap} accent="#F59E0B" />
                <RollUpKPI label="Clients payants" value={global.clients_total || 0} icon={Users} accent="#10B981" />
                <RollUpKPI label="Essais actifs" value={global.trials_total || 0} icon={CalendarClock} accent="#3B82F6" />
                <RollUpKPI label="Apps gérées" value={global.apps_count || 0} icon={Package} accent="#8B5CF6" />
              </div>
              {global.most_profitable_app && (
                <div className="mt-4 inline-flex items-center gap-2 text-xs text-zinc-400">
                  <Award className="w-3.5 h-3.5 text-amber-400" />
                  App la plus rentable actuellement :{" "}
                  <span className="text-white font-medium uppercase tracking-wider">
                    {apps.find((a) => a.code === global.most_profitable_app)?.name || global.most_profitable_app}
                  </span>
                </div>
              )}
            </div>

            {/* Per-app cards */}
            {apps.length === 0 ? (
              <div className="border border-dashed border-white/10 p-10 text-center space-y-2">
                <Briefcase className="w-8 h-8 text-zinc-600 mx-auto" />
                <div className="text-sm text-zinc-300">Aucune application enregistrée</div>
                <Link to="/app/settings" className="text-xs text-amber-400 hover:underline">Ajouter une app depuis les paramètres</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {apps.map((app) => <AppCard key={app.code} app={app} />)}
              </div>
            )}
          </>
        )}
      </div>
    </ConsoleShell>
  );
}

function RollUpKPI({ label, value, icon: Icon, accent }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        {label}
      </div>
      <div className="text-2xl font-light text-white">{value}</div>
    </div>
  );
}

function AppCard({ app }) {
  const k = app.kpis;
  const color = app.primary_color;
  const isFinanceOnly = app.coverage_mode === "finance_only";

  return (
    <div
      className="border border-white/10 bg-white/[0.02] hover:border-white/20 transition-all overflow-hidden group"
      data-testid={`app-card-${app.code}`}
    >
      {/* Header */}
      <div className="p-5 border-b border-white/5 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 grid place-items-center text-2xl border"
            style={{ borderColor: `${color}40`, background: `${color}10` }}
          >
            {app.icon}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-lg font-semibold text-white uppercase tracking-wide" style={{ color }}>{app.name}</div>
              {app.is_primary && <span className="text-[9px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 uppercase tracking-wider">Principal</span>}
              {isFinanceOnly && <span className="text-[9px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 uppercase tracking-wider">Finance seule</span>}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">{app.sector}</div>
            {app.description && <div className="text-[11px] text-zinc-600 mt-1 line-clamp-1">{app.description}</div>}
          </div>
        </div>
        {app.base_url && (
          <a
            href={app.base_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-zinc-500 hover:text-white p-1"
            title="Ouvrir l'app"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* KPIs grid */}
      <div className="grid grid-cols-3 gap-0 border-b border-white/5">
        <KPICell label={app.customer_label} value={k.customers} sub={`${k.clients} payants`} accent={color} />
        <KPICell label="Essais actifs" value={k.trials_active} sub={k.at_risk > 0 ? `${k.at_risk} < 7j` : "—"} accent="#3B82F6" />
        <KPICell label="Licences" value={k.licenses_sold} sub={k.cancelled > 0 ? `${k.cancelled} résiliés` : "—"} accent="#8B5CF6" />
      </div>

      <div className="grid grid-cols-3 gap-0 border-b border-white/5">
        <KPICell label="MRR" value={fmtEUR(k.mrr)} sub={`ARR ${fmtEUR(k.arr)}`} accent="#10B981" mono />
        <KPICell label="CA du mois" value={fmtEUR(k.revenue_month)} sub={`${fmtEUR(k.revenue_year)} YTD`} accent="#F59E0B" mono />
        <KPICell label="Impayés" value={k.overdue} sub={k.overdue_amount > 0 ? `${fmtEUR(k.overdue_amount)} à recouvrer` : "—"} accent={k.overdue > 0 ? "#EF4444" : "#71717A"} />
      </div>

      {/* Upcoming renewals */}
      {k.upcoming_renewals && k.upcoming_renewals.length > 0 && (
        <div className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 flex items-center gap-2">
            <CalendarClock className="w-3 h-3" />
            Échéances dans 30 jours ({k.upcoming_renewals.length})
          </div>
          <div className="space-y-1.5">
            {k.upcoming_renewals.slice(0, 4).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-white/5 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-200 truncate">{r.user_name}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{r.user_email}</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="text-zinc-300 font-mono">{fmtEUR(r.amount)}</div>
                  <div
                    className={`text-[10px] ${r.days_until_due <= 3 ? "text-amber-400" : "text-zinc-500"}`}
                  >
                    {r.days_until_due === 0 ? "aujourd'hui" : `dans ${r.days_until_due}j`} · {fmtDate(r.due_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA footer */}
      <div className="p-4 border-t border-white/5 flex items-center justify-between text-xs">
        <div className="text-zinc-500">
          Coverage : <span className="text-zinc-300">{isFinanceOnly ? "Finance seule" : "Complète"}</span>
        </div>
        <Link
          to="/app/subscriptions"
          className="inline-flex items-center gap-1 text-zinc-400 hover:text-white transition"
          data-testid={`view-subs-${app.code}`}
        >
          Voir les abonnements <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function KPICell({ label, value, sub, accent, mono }) {
  return (
    <div className="px-4 py-3 border-r border-white/5 last:border-r-0">
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">{label}</div>
      <div className={`text-xl text-white ${mono ? "font-mono font-light" : "font-light"}`} style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
